import {
  CompanyShareableType,
  Contact_Update_Destroyable,
  deleteV1CompaniesCompanyIdCompanySharesId,
  GeofenceTag,
  GeofenceType,
  getV1CompaniesCompanyIdSitesSiteIdAdditionalGeofences,
  patchV1CompaniesCompanyIdSitesId,
  patchV1CompaniesCompanyIdSitesSiteIdAdditionalGeofencesBulkAssign,
  patchV1CompaniesCompanyIdSitesSiteIdContactsBulkAssign,
  postV1CompaniesCompanyIdCompanyShares,
  postV1CompaniesCompanyIdSites,
  Site_Create,
} from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { useState } from 'react';

import { MAX_API_PAGE_SIZE } from '~constants/consts';
import { Geofence, Site } from '~hooks/useSites/models';
import { generateCircleAttributes, generatePolygonGeoJson } from '~hooks/useSites/utils';
import connection from '~services/connectionModule';
import { PaginationQuery } from '~services/pagination';
import { useStores } from '~store';
import { isAnyFieldDirty } from '~utils/buildPatch';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import {
  CompanyShareFormFields,
  ContactFormFields,
  type SiteFormV2DirtyFields,
  SiteFormV2Fields,
} from './types';

export const useSiteFormV2 = () => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);

  const { companyAssetsStore, userStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);

  const createNewSite = async (data: SiteFormV2Fields) => {
    const siteCompanyId = data.company?.id || companyId;
    const additionalGeofences = data.additionalGeofences;
    const [lat, lon] = data.latLon
      .split(',')
      .map((v) => v.trim())
      .map((v) => parseFloat(v));
    const primaryCircleCenter = data.circleCenter
      ? { lat: data.circleCenter.lat, lon: data.circleCenter.lng }
      : { lat, lon };

    setIsUpdating(true);
    // The Site model is not well-typed and has a lot of 'any' types. This is a temporary solution.
    const payload = Site.deparseCreate(data) as Site_Create;

    /*
     * The Site parse and deparse methods are littered with 'any' and hard to work with. When we don't have to support these variants,
     * we should move this logic into the Site model and fix the types.
     */
    if (data.drawingCoordinates?.length) {
      payload['next_billion_geofence'] = {
        geofence: {
          name: data.name ?? '',
          type: GeofenceType.POLYGON,
          geojson: generatePolygonGeoJson(data.drawingCoordinates),
        },
        moving_geofence: false,
      };
    }
    if (data.geofenceType?.name === 'Circle') {
      payload['next_billion_geofence'] = {
        geofence: {
          name: data.name ?? '',
          type: GeofenceType.CIRCLE,
          ...generateCircleAttributes({
            center: primaryCircleCenter,
            radiusInFeet: Number(data.radius),
          }),
        },
        moving_geofence: false,
      };
    }
    if (data.geofenceType?.name === 'Equipment') {
      payload['next_billion_geofence'] = {
        geofence: {
          name: data.name ?? '',
          type: GeofenceType.CIRCLE,
          ...generateCircleAttributes({
            center: primaryCircleCenter,
            radiusInFeet: Number(data.radius),
          }),
        },
        moving_geofence: true,
      };
      payload['moving_site'] = {
        equipment_id: data.equipment?.id ?? '',
      };
    }
    try {
      const resp = await postV1CompaniesCompanyIdSites({
        path: { 'company-id': siteCompanyId },
        body: payload,
      });
      const site = Site.parse(resp.data.data);
      try {
        const geofenceData = additionalGeofences.map((geofence) => ({
          tag: geofence.tag?.id as GeofenceTag,
          moving_geofence: geofence.geofenceType?.name === 'Equipment',
          geofence: {
            id: geofence.geofenceId ?? undefined,
            name: data.name ?? '',
            ...(geofence.geofenceType?.name === 'Polygon'
              ? {
                  type: GeofenceType.POLYGON,
                  geojson: generatePolygonGeoJson(geofence.drawingCoordinates),
                }
              : {
                  type: GeofenceType.CIRCLE,
                  ...generateCircleAttributes({
                    center: {
                      lat: geofence.drawingCoordinates?.[0]?.lat ?? lat,
                      lon: geofence.drawingCoordinates?.[0]?.lng ?? lon,
                    },
                    radiusInFeet: Number(geofence.radius),
                  }),
                }),
          },
        }));
        const geofenceResp =
          await patchV1CompaniesCompanyIdSitesSiteIdAdditionalGeofencesBulkAssign({
            path: { 'company-id': siteCompanyId, 'site-id': site.id },
            body: {
              additional_geofences: geofenceData,
            },
          });
        const updatedGeofences = geofenceResp.data.data.map(Geofence.parse);
        companyAssetsStore.setActiveSiteAdditionalGeoFences(updatedGeofences);

        await bulkAssignSiteContacts(siteCompanyId, site.id, data.contacts);

        if (data.companyShares && data.companyShares.length > 0) {
          await handleCompanyShares(siteCompanyId, site.id, data.companyShares);
        }
      } catch (error) {
        connection.handleRequestError(
          error,
          t('error_messages.sites.failed_to_assign_additional_geofences'),
        );
      }
      setIsUpdating(false);
      return site;
    } catch (error) {
      setIsUpdating(false);
      connection.handleRequestError(error, t('error_messages.sites.failed_to_create'));
    }
  };

  const updateSite = async (
    data: SiteFormV2Fields & { id: string },
    dirtyFields: SiteFormV2DirtyFields,
  ) => {
    const siteCompanyId = data.company?.id || companyId;
    const additionalGeofences = data.additionalGeofences;
    const [lat, lon] = data.latLon
      .split(',')
      .map((v) => v.trim())
      .map((v) => parseFloat(v));

    setIsUpdating(true);

    const payload = Site.deparseSiteFormV2Update(data, dirtyFields);

    try {
      const resp = await patchV1CompaniesCompanyIdSitesId({
        path: { 'company-id': siteCompanyId, id: data.id },
        body: payload,
      });
      const site = Site.parse(resp.data.data);
      try {
        // Radii round-trip through meters→feet→meters, so resubmitting untouched
        // geofences would rewrite the stored radius with a rounded value.
        if (isAnyFieldDirty(dirtyFields, ['additionalGeofences', 'name', 'latLon'])) {
          const geofenceData = additionalGeofences.map((geofence) => ({
            tag: geofence.tag?.id as GeofenceTag,
            moving_geofence: geofence.geofenceType?.name === 'Equipment',
            geofence: {
              id: geofence.geofenceId ?? undefined,
              name: data.name ?? '',
              ...(geofence.geofenceType?.name === 'Polygon'
                ? {
                    type: GeofenceType.POLYGON,
                    geojson: generatePolygonGeoJson(geofence.drawingCoordinates),
                  }
                : {
                    type: GeofenceType.CIRCLE,
                    ...generateCircleAttributes({
                      center: {
                        lat: geofence.drawingCoordinates?.[0]?.lat ?? lat,
                        lon: geofence.drawingCoordinates?.[0]?.lng ?? lon,
                      },
                      radiusInFeet: Number(geofence.radius),
                    }),
                  }),
            },
          }));
          const geofenceResp =
            await patchV1CompaniesCompanyIdSitesSiteIdAdditionalGeofencesBulkAssign({
              path: { 'company-id': siteCompanyId, 'site-id': site.id },
              body: {
                additional_geofences: geofenceData,
              },
            });
          const updatedGeofences = geofenceResp.data.data.map(Geofence.parse);
          companyAssetsStore.setActiveSiteAdditionalGeoFences(updatedGeofences);
        }

        await bulkAssignSiteContacts(siteCompanyId, site.id, data.contacts);

        await handleCompanyShares(siteCompanyId, site.id, data.companyShares || []);
      } catch (error) {
        connection.handleRequestError(
          error,
          t('error_messages.sites.failed_to_assign_additional_geofences'),
        );
      }
      setIsUpdating(false);
      return site;
    } catch (error) {
      setIsUpdating(false);
      connection.handleRequestError(error, t('error_messages.sites.failed_to_update'));
    }
  };

  const getAdditionalGeoFencesForSite = async (
    companyId: string,
    id: string,
    signal?: AbortSignal,
  ) => {
    setIsLoading(true);
    const params: PaginationQuery = {
      'page[limit]': MAX_API_PAGE_SIZE,
    };
    try {
      const resp = await getV1CompaniesCompanyIdSitesSiteIdAdditionalGeofences({
        path: { 'company-id': companyId, 'site-id': id },
        query: params,
        signal,
      });
      const additionalGeofences = resp.data.data.map(Geofence.parse);
      companyAssetsStore.setActiveSiteAdditionalGeoFences(additionalGeofences);
      setIsLoading(false);
      return additionalGeofences;
    } catch (error) {
      if (signal?.aborted) {
        throw error;
      }

      setIsLoading(false);
      connection.handleRequestError(
        error,
        t('error_messages.sites.failed_to_fetch_additional_geofences'),
      );
    }
  };

  const bulkAssignSiteContacts = async (
    companyId: string,
    siteId: string,
    contacts: ContactFormFields[],
  ) => {
    const payload = contacts
      .filter((contact) => contact.id || contact.name || contact.phone)
      .map((contact) => ({
        id: contact.id,
        name: contact.name,
        phone: contact.phone,
        _destroy: contact.isDeleted ? 1 : undefined,
      })) as Contact_Update_Destroyable[];

    try {
      await patchV1CompaniesCompanyIdSitesSiteIdContactsBulkAssign({
        path: { 'company-id': companyId, 'site-id': siteId },
        body: { contacts: payload },
      });
    } catch (error) {
      connection.handleRequestError(
        error,
        t('error_messages.sites.failed_to_update_contacts'),
      );
    }
  };

  const handleCompanyShares = async (
    companyId: string,
    siteId: string,
    companyShares: CompanyShareFormFields[],
  ) => {
    const toCreate = companyShares.reduce(
      (acc, companyShare) => {
        if (
          !companyShare.id &&
          !companyShare.isDeleted &&
          companyShare.receiverCompany?.id
        ) {
          acc.push({
            receiver_company_id: companyShare.receiverCompany.id,
            company_shareable_id: siteId,
            company_shareable_type: CompanyShareableType.SITE,
          });
        }
        return acc;
      },
      [] as Array<{
        receiver_company_id: string;
        company_shareable_id: string;
        company_shareable_type: CompanyShareableType;
      }>,
    );

    const toDelete = companyShares.reduce(
      (acc, companyShare) => {
        if (companyShare.id && companyShare.isDeleted) {
          acc.push({ companyShareId: companyShare.id });
        }
        return acc;
      },
      [] as Array<{ companyShareId: string }>,
    );

    const toDeletePromises = toDelete.map((payload) => {
      return deleteV1CompaniesCompanyIdCompanySharesId({
        path: { 'company-id': companyId, id: payload.companyShareId },
      });
    });

    await Promise.all(toDeletePromises);

    const toCreatePromises = toCreate.map((payload) => {
      return postV1CompaniesCompanyIdCompanyShares({
        path: { 'company-id': companyId },
        body: payload,
      });
    });

    await Promise.all(toCreatePromises);
  };

  return {
    createNewSite,
    getAdditionalGeoFencesForSite,
    isLoading,
    isUpdating,
    updateSite,
  };
};
